import { useCallback, type MouseEvent as ReactMouseEvent } from "react"
import { scheduleOpenIngestionConversation } from '@/lib/open-ingestion-conversation'
import { enqueueUpdateWorkSpecKickoff } from '@/lib/update-work-spec-kickoff'
import { toast } from "sonner"
import {
  fetchWithAuth,
  BasecampWorkspaceBlockedError,
  handleBasecampWorkspaceBlockedError,
  type BasecampEnabledWorkspace,
} from "@/lib/api"
import { resolveBasecampAppUrl } from "@/lib/basecamp-external-url"
import { extractBasecampUrlInfo } from "./command-palette.utils"

/** Resolve a browser Basecamp todo URL for ingest when the palette only has a numeric ID. */
async function resolveBasecampTodoIngestUrl(
  basecampId: string,
  trimmedSearch: string,
): Promise<string | null> {
  if (trimmedSearch.includes("basecamp.com")) {
    return trimmedSearch.split("#")[0].split("?")[0]
  }

  try {
    const res = await fetchWithAuth(`/api/basecamp/search-todo/${basecampId}`)
    if (!res.ok) return null
    const data = (await res.json()) as {
      found?: boolean
      todo?: {
        id?: string
        appUrl?: string | null
        bucketId?: string | null
        projectId?: string | null
        recordType?: 'todo' | 'todolist'
        cachedData?: { app_url?: string } | null
      }
    }
    if (!data.found || !data.todo) return null

    const raw =
      data.todo.appUrl ||
      data.todo.cachedData?.app_url ||
      null
    const bucketId = data.todo.bucketId || data.todo.projectId
    const recordType = data.todo.recordType === 'todolist' ? 'todolist' : 'todo'
    if (raw || bucketId) {
      return resolveBasecampAppUrl({
        recordId: data.todo.id || basecampId,
        projectId: bucketId ? String(bucketId) : null,
        recordType,
        candidateUrl: raw,
      })
    }
  } catch (err) {
    console.warn("[Command Palette] search-todo lookup failed:", err)
  }

  return null
}

export interface UseBasecampHandlersDeps {
  setOpen: (open: boolean) => void
  onDocumentSelect?: (documentId: string, event?: React.MouseEvent) => void
  setLoading: (loading: boolean) => void
  trimmedSearch: string
  activeWorkspaceIds?: string[]
  /** Add a workspace to the sidebar scope (Basecamp blocked toast action). */
  onAddWorkspaceToScope?: (workspaceId: string) => void
}

export function useBasecampHandlers(deps: UseBasecampHandlersDeps) {
  const {
    setOpen,
    onDocumentSelect,
    setLoading,
    trimmedSearch,
    activeWorkspaceIds = [],
    onAddWorkspaceToScope,
  } = deps
  const ingestionOrganizationId = activeWorkspaceIds[0]

  /** Run in the requested workspace; if Basecamp is off there but on in another active workspace, use that. */
  const runInBasecampWorkspace = useCallback(
    async <T>(organizationId: string | undefined, run: (orgId?: string) => Promise<T>): Promise<T> => {
      try {
        return await run(organizationId)
      } catch (error) {
        if (error instanceof BasecampWorkspaceBlockedError) {
          const inScope = error.enabledWorkspaces.find(
            (w) => w.id !== organizationId && activeWorkspaceIds.includes(w.id),
          )
          if (inScope) return run(inScope.id)
        }
        throw error
      }
    },
    [activeWorkspaceIds],
  )

  const retryInWorkspace = useCallback(
    (retry: (orgId: string) => void) => (workspace: BasecampEnabledWorkspace) => {
      onAddWorkspaceToScope?.(workspace.id)
      retry(workspace.id)
    },
    [onAddWorkspaceToScope],
  )

  const handleBasecampUpdate = useCallback(async (
    basecampId: string,
    existingDocId: string,
    itemType?: 'todo' | 'message' | 'inbox_forward',
    organizationIdOverride?: string,
  ) => {
    const targetOrganizationId = organizationIdOverride ?? ingestionOrganizationId
    const onBlocked = retryInWorkspace((orgId) =>
      handleBasecampUpdate(basecampId, existingDocId, itemType, orgId),
    )
    try {
      const typeLabel = itemType === 'message' || itemType === 'inbox_forward' ? 'message' : 'todo';

      const loadingToast = toast.loading(`Looking up ${typeLabel}...`, { duration: 8000 });

      let sourceUrl: string | null = null;
      let workSpecDocumentId: string | null = null;

      try {
        const apiUrl = existingDocId.includes('/')
          ? `/api/documents/by-path?path=${encodeURIComponent(existingDocId)}`
          : `/api/documents/${existingDocId}`;

        const response = await fetchWithAuth(apiUrl);
        if (response.ok) {
          const docData = await response.json();
          workSpecDocumentId = docData.id ?? null;
          sourceUrl = docData.frontmatter?.source_url || docData.frontmatter?.url || null;
          console.log("[Command Palette] Found source URL in document:", sourceUrl);
        }
      } catch (err) {
        console.error("[Command Palette] Failed to fetch document:", err);
      }

      if (!sourceUrl && trimmedSearch.includes('basecamp.com')) {
        sourceUrl = trimmedSearch;
        console.log("[Command Palette] Using URL from search field:", sourceUrl);
      }

      toast.dismiss(loadingToast);

      if (!sourceUrl) {
        toast.error('No Basecamp URL found', {
          description: 'Document has no source_url in frontmatter. Please paste the full Basecamp URL.',
          duration: 5000
        });
        return;
      }

      const isMessage = itemType === 'message' || itemType === 'inbox_forward' ||
                       sourceUrl.includes('/messages/') || sourceUrl.includes('/inbox_forwards/');

      // Update flow: open existing work spec in a new main-panel tab while ingestion runs in agent panel
      if (!isMessage && workSpecDocumentId && onDocumentSelect) {
        onDocumentSelect(
          workSpecDocumentId,
          { metaKey: true, ctrlKey: false } as ReactMouseEvent,
        );
      }

      try {
        const urlInfo = extractBasecampUrlInfo(sourceUrl!);

        if (!isMessage && urlInfo.projectId && urlInfo.todoId) {
          const tabId = `basecamp-todo-${urlInfo.todoId}`;
          window.dispatchEvent(new CustomEvent('integration-view-change', {
            detail: {
              tabId: tabId,
              title: 'Loading...',
              type: 'integration-basecamp-todo',
              integrationData: {
                integrationId: 'basecamp',
                projectId: urlInfo.projectId,
                todoId: urlInfo.todoId
              }
            }
          }));
        }

        if (isMessage) {
          const { ingestBasecampMessage } = await import('@/lib/api');

          window.dispatchEvent(new CustomEvent('thinking-mode-event', {
            detail: { type: 'thinking_start', message: 'Starting Basecamp message ingestion...' }
          }));

          const ingestionPromise = runInBasecampWorkspace(targetOrganizationId, (orgId) =>
            ingestBasecampMessage(sourceUrl!, { forceRefresh: true, organizationId: orgId }),
          );

          ingestionPromise.then((result) => {
            scheduleOpenIngestionConversation(
              result.conversationId,
              result.threadId,
              result.title,
            )
          }).catch((error) => {
            if (handleBasecampWorkspaceBlockedError(error, onBlocked)) return;
            scheduleOpenIngestionConversation(
              error.conversationId,
              error.threadId,
            )
          });
        } else {
          const {
            ingestBasecampTodo,
            applyUpdateWorkSpecModeUi,
            handleUpdateWorkSpecBlockedError,
            isAgentUpdateWorkSpecResult,
          } = await import('@/lib/api');

          const isUpdate = Boolean(workSpecDocumentId);
          if (!isUpdate) {
            window.dispatchEvent(new CustomEvent('thinking-mode-event', {
              detail: { type: 'thinking_start', message: 'Starting Basecamp todo ingestion...' }
            }));
          }

          const ingestionPromise = runInBasecampWorkspace(targetOrganizationId, (orgId) =>
            ingestBasecampTodo(sourceUrl!, {
              forceRefresh: isUpdate,
              organizationId: orgId,
              ...(isUpdate ? { mode: 'agent' as const } : {}),
            }),
          );

          ingestionPromise.then((result) => {
            applyUpdateWorkSpecModeUi(result);
            scheduleOpenIngestionConversation(
              result.conversationId,
              result.threadId,
              result.title,
            );
            if (isAgentUpdateWorkSpecResult(result) && result.conversationId) {
              enqueueUpdateWorkSpecKickoff({
                targetConversationId: result.conversationId,
                targetThreadId: result.threadId,
                documentId: result.documentId,
                skillId: 'basecamp-todo',
              });
            }
          }).catch((error) => {
            if (handleUpdateWorkSpecBlockedError(error)) return;
            if (handleBasecampWorkspaceBlockedError(error, onBlocked)) return;
            scheduleOpenIngestionConversation(
              error.conversationId,
              error.threadId,
            )
          });
        }
      } catch (error: unknown) {
        const { handleUpdateWorkSpecBlockedError } = await import('@/lib/api');
        if (handleUpdateWorkSpecBlockedError(error)) return;
        if (handleBasecampWorkspaceBlockedError(error, onBlocked)) return;
        const err = error as { conversationId?: string; threadId?: string; message?: string };
        if (err.conversationId) {
          scheduleOpenIngestionConversation(err.conversationId, err.threadId);
        } else {
          toast.error('Update failed', {
            description: err.message || 'Failed to update'
          });
        }
      }
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast.error('Update failed', {
        description: err.message || 'Failed to initiate update'
      });
    }
  }, [trimmedSearch, onDocumentSelect, ingestionOrganizationId, runInBasecampWorkspace, retryInWorkspace]);

  const handleBasecampTodoIngestion = useCallback(async (basecampId: string, organizationIdOverride?: string) => {
    const targetOrganizationId = organizationIdOverride ?? ingestionOrganizationId
    const onBlocked = retryInWorkspace((orgId) => handleBasecampTodoIngestion(basecampId, orgId))
    try {
      console.log("[Command Palette] Ingesting Basecamp todo ID:", basecampId)

      const fullUrl = await resolveBasecampTodoIngestUrl(basecampId, trimmedSearch)

      if (!fullUrl) {
        toast.error('Full Basecamp URL Required for New Ingestion', {
          description: `Paste the complete URL: https://app.basecamp.com/.../todos/${basecampId}\n\nTip: Open the todo in Basecamp first to sync cache, or paste the URL. "Update Work Spec" works with ID when a work spec already exists.`,
          duration: 8000
        });
        return;
      }

      setOpen(false);

      window.dispatchEvent(new CustomEvent('thinking-mode-event', {
        detail: {
          type: 'thinking_start',
          message: 'Starting Basecamp todo ingestion...'
        }
      }));

      const {
        ingestBasecampTodo,
        applyUpdateWorkSpecModeUi,
        handleUpdateWorkSpecBlockedError,
      } = await import('@/lib/api');

      try {
        const ingestionPromise = runInBasecampWorkspace(targetOrganizationId, (orgId) =>
          ingestBasecampTodo(fullUrl, { organizationId: orgId }),
        );

        ingestionPromise.then((result) => {
          console.log("[Command Palette] Ingestion result:", result);
          applyUpdateWorkSpecModeUi(result);
          scheduleOpenIngestionConversation(
            result.conversationId,
            result.threadId,
            result.title,
          )
        }).catch((error) => {
          if (handleUpdateWorkSpecBlockedError(error)) return;
          if (error instanceof BasecampWorkspaceBlockedError) return;
          scheduleOpenIngestionConversation(
            error.conversationId,
            error.threadId,
          )
        });

        const result = await ingestionPromise;
        applyUpdateWorkSpecModeUi(result);

        if (result.status === 'duplicate_detected') {
          // Chat panel already opened above - no toast needed
        } else if (!result.success) {
          if (!result.conversationId) {
            toast.error('Ingestion failed', {
              description: result.error || 'Unknown error',
              duration: 7000
            });
          }
        }

      } catch (ingestionError: unknown) {
        console.error("[Command Palette] Ingestion failed:", ingestionError);

        if (handleUpdateWorkSpecBlockedError(ingestionError)) return;
        if (handleBasecampWorkspaceBlockedError(ingestionError, onBlocked)) return;

        const error = ingestionError as { conversationId?: string; threadId?: string; status?: number; auth_url?: string };
        scheduleOpenIngestionConversation(error.conversationId, error.threadId);

        if (error.status === 401 && error.auth_url) {
          const authWindow = window.open(error.auth_url, 'basecamp-auth', 'width=600,height=700');

          toast.info('Basecamp Authentication Required', {
            description: 'Please authorize in the popup window. Will retry after you authenticate.',
            duration: 10000,
          });

          const authCheckInterval = setInterval(async () => {
            try {
              const testResponse = await fetchWithAuth('/api/basecamp/ingest-todo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: fullUrl })
              });

              if (testResponse.ok || testResponse.status !== 401) {
                clearInterval(authCheckInterval);
                authWindow?.close();

                toast.success('Authentication successful!', {
                  description: 'Retrying ingestion...',
                  duration: 2000
                });

                setTimeout(() => handleBasecampTodoIngestion(basecampId), 1000);
              }
            } catch {
              // Still not authed, keep polling
            }
          }, 2000);

          setTimeout(() => {
            clearInterval(authCheckInterval);
            authWindow?.close();
          }, 120000);

          return;
        }

        const errorMessage = ingestionError instanceof Error ? ingestionError.message : 'Unknown error';

        toast.error('Ingestion Failed', {
          description: errorMessage,
          duration: 8000,
          action: {
            label: 'Details',
            onClick: () => {
              console.log('Full error:', ingestionError);
              toast.info('Check Console', {
                description: 'Full error details logged to browser console'
              });
            }
          }
        });
      } finally {
        setLoading(false);
      }

    } catch (error) {
      console.error("[Command Palette] Basecamp ingestion error:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.error('Unexpected Error', {
        description: errorMessage
      });

      setLoading(false);
    }
  }, [trimmedSearch, setOpen, onDocumentSelect, setLoading, ingestionOrganizationId, runInBasecampWorkspace, retryInWorkspace]);

  const handleBasecampMessageIngestion = useCallback(async (
    basecampId: string,
    basecampType: string,
    urlOverride?: string,
    organizationIdOverride?: string,
  ) => {
    const targetOrganizationId = organizationIdOverride ?? ingestionOrganizationId
    try {
      console.log("[Command Palette] Ingesting Basecamp message/inbox forward ID:", basecampId, "Type:", basecampType)

      const fullUrl = urlOverride || (trimmedSearch.includes('basecamp.com')
        ? trimmedSearch
        : null);

      if (!fullUrl) {
        const urlType = basecampType === 'inbox_forward' ? 'inbox_forwards' : 'messages'
        toast.error('Full Basecamp URL Required for New Ingestion', {
          description: `Paste the complete URL: https://app.basecamp.com/.../${urlType}/${basecampId}\n\nTip: If this message already exists, use "Update Message" instead (just needs ID)`,
          duration: 8000
        });
        return;
      }

      setOpen(false);

      window.dispatchEvent(new CustomEvent('thinking-mode-event', {
        detail: {
          type: 'thinking_start',
          message: 'Starting Basecamp message ingestion...'
        }
      }));

      const { ingestBasecampMessage } = await import('@/lib/api');

      try {
        const ingestionPromise = runInBasecampWorkspace(targetOrganizationId, (orgId) =>
          ingestBasecampMessage(fullUrl, { forceRefresh: true, organizationId: orgId }),
        );

        ingestionPromise.then((result) => {
          scheduleOpenIngestionConversation(
            result.conversationId,
            result.threadId,
            result.title,
          )
        }).catch((error) => {
          if (error instanceof BasecampWorkspaceBlockedError) return;
          scheduleOpenIngestionConversation(
            error.conversationId,
            error.threadId,
          )
        });

        const result = await ingestionPromise;

        console.log("[Command Palette] Message ingestion result:", result);

        if (result.status === 'duplicate_detected') {
          scheduleOpenIngestionConversation(
            result.conversationId,
            result.threadId,
            result.title,
          )
        } else if (!result.success) {
          if (!result.conversationId) {
            toast.error('Ingestion failed', {
              description: result.error || 'Unknown error',
              duration: 7000
            });
          }
        }

      } catch (ingestionError: unknown) {
        console.error("[Command Palette] Message ingestion failed:", ingestionError);

        if (
          handleBasecampWorkspaceBlockedError(
            ingestionError,
            retryInWorkspace((orgId) =>
              handleBasecampMessageIngestion(basecampId, basecampType, fullUrl, orgId),
            ),
          )
        ) {
          return;
        }

        const error = ingestionError as { status?: number; auth_url?: string };
        if (error.status === 401 && error.auth_url) {
          const authWindow = window.open(error.auth_url, 'basecamp-auth', 'width=600,height=700');

          toast.info('Basecamp Authentication Required', {
            description: 'Please authorize in the popup window. Will retry after you authenticate.',
            duration: 10000,
          });

          const authCheckInterval = setInterval(async () => {
            try {
              const testResponse = await fetchWithAuth('/api/basecamp/ingest-message', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: fullUrl })
              });

              if (testResponse.ok || testResponse.status !== 401) {
                clearInterval(authCheckInterval);
                authWindow?.close();

                toast.success('Authentication successful!', {
                  description: 'Retrying ingestion...',
                  duration: 2000
                });

                setTimeout(() => handleBasecampMessageIngestion(basecampId, basecampType, fullUrl), 1000);
              }
            } catch {
              // Still not authed, keep polling
            }
          }, 2000);

          setTimeout(() => {
            clearInterval(authCheckInterval);
            authWindow?.close();
          }, 120000);

          return;
        }

        const errorMessage = ingestionError instanceof Error ? ingestionError.message : 'Unknown error';

        toast.error('Ingestion Failed', {
          description: errorMessage,
          duration: 8000,
          action: {
            label: 'Details',
            onClick: () => {
              console.log('Full error:', ingestionError);
              toast.info('Check Console', {
                description: 'Full error details logged to browser console'
              });
            }
          }
        });
      } finally {
        setLoading(false);
      }

    } catch (error) {
      console.error("[Command Palette] Basecamp message ingestion error:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.error('Unexpected Error', {
        description: errorMessage
      });

      setLoading(false);
    }
  }, [trimmedSearch, setOpen, onDocumentSelect, setLoading, ingestionOrganizationId, runInBasecampWorkspace, retryInWorkspace]);

  return {
    handleBasecampUpdate,
    handleBasecampTodoIngestion,
    handleBasecampMessageIngestion
  }
}
